import type { Random } from '../core/Random';
import type { Outcome, Store } from '../state/Store';
import type { SpotId } from '../world/layout';
import type { IconToken } from '../icons/index';

export type SfxName =
  | 'canOpen'
  | 'gulp'
  | 'burp'
  | 'cash'
  | 'clank'
  | 'boing'
  | 'fail'
  | 'airhorn'
  | 'pickup'
  | 'bark'
  | 'yip'
  | 'siren'
  | 'tornado'
  | 'whoop'
  | 'crunch'
  | 'splash'
  | 'thud'
  | 'door'
  | 'zap'
  | 'scratch'
  | 'ding'
  | 'error'
  | 'crowd'
  | 'horn'
  | 'snore'
  | 'phone'
  | 'boom'
  | 'whistle'
  | 'semiHorn'
  | 'gunshot'
  | 'ting'
  | 'lever'
  | 'thunk'
  | 'ufo'
  | 'moo';

/** Effects content can trigger in the 3D world. Implemented by the Game. */
export interface WorldEffects {
  tornado(): void;
  cops(): void;
  lostDog(): void;
  fireworks(): void;
  scatterScrap(n: number): void;
  spawnCans(n: number): void;
  shake(intensity: number, seconds: number): void;
  teleportPlayer(spot: SpotId): void;
  /** Where the player is: the main park or the Back Forty. */
  zone(): 'park' | 'backForty';
}

export interface ContentUi {
  openScene(scene: Scene): void;
  openShop(shop: 'gas' | 'pawn' | 'junkyard' | 'office' | 'bigdave' | 'jobs' | 'booze' | 'cooter'): void;
  watchTv(): void;
  /** Cornhole against Lil' Ricky; a bet of 0 is practice. */
  playCornhole(bet: number): void;
  /** Bar games, the rodeo and the gun range. The bet (or entry fee) is taken up front. */
  play(game: 'darts' | 'armwrestle' | 'poker' | 'mechbull' | 'rodeo' | 'shoot', bet: number): void;
}

/** Everything content (dialogue, events) is allowed to touch. */
export interface GameContext {
  store: Store;
  rng: Random;
  world: WorldEffects;
  ui: ContentUi;
  sfx(name: SfxName): void;
}

/** Return `true` if a choice is available, or a short reason it isn't. */
export type Requirement = (ctx: GameContext) => true | string;

export type ChoiceResult = Outcome | Scene | string | void;

export interface Choice {
  label: string;
  /** Short cost/benefit hint shown on the button, e.g. "-$20". */
  hint?: string;
  requires?: Requirement;
  run: (ctx: GameContext) => ChoiceResult;
}

export interface Speaker {
  id: string;
  name: string;
  /** Base pitch (Hz) for the chatter blips. */
  voice?: number;
}

export interface Scene {
  title: string;
  text: string;
  choices: Choice[];
  speaker?: Speaker;
  /** Big icon illustration for speaker-less scenes. */
  art?: IconToken;
  /** Label for the leave/close button; `false` forces a choice. */
  leave?: string | false;
}

export function isScene(result: ChoiceResult): result is Scene {
  return typeof result === 'object' && result !== null && 'choices' in result;
}

// --- requirement helpers ------------------------------------------------------

export const needMoney =
  (amount: number): Requirement =>
  ({ store }) =>
    store.canAfford(amount) || `Need $${amount}`;

export const needItem =
  (id: Parameters<Store['has']>[0], n = 1, label?: string): Requirement =>
  ({ store }) =>
    store.has(id, n) || `Need ${n} ${label ?? id}`;

export const onceToday =
  (key: string, reason = 'Already did that today'): Requirement =>
  ({ store }) =>
    !store.doneToday(key) || reason;

export const all =
  (...reqs: Requirement[]): Requirement =>
  (ctx) => {
    for (const r of reqs) {
      const ok = r(ctx);
      if (ok !== true) return ok;
    }
    return true;
  };
